// Shared SMS verification state machine + code-input renderer.
// Used by Signup (initial account creation) and My Profile's phone-change
// flow. Both backends expose the same contract (sessionId → verify → token),
// so a single state machine drives both.
//
// Architecture: the parent owns the phone INPUT and the SEND button (which
// it positions next to the input), and asks the hook to drive the send/
// verify state machine. The PhoneVerificationCodeSection component renders
// the code-input field, timer, verify button, and status message.

import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiError } from '@/api/error';

export interface PhoneVerificationLabels {
  sendCode: string;
  resend: string;
  codeLabel: string;
  codePlaceholder?: string;
  verify: string;
  verified: string;
  codeInvalid: string;
  codeExpired: string;
  alreadyVerified: string;
  sessionExpired: string;
  serverError: string;
  // SHCT-389: text shown in the timer slot once verified (signup prototype → "인증완료").
  // When omitted, the countdown simply stops/blanks on verify (My Profile pattern).
  verifiedTimer?: string;
}

export interface PhoneVerificationDeps {
  phone: string;
  cooldownSec?: number; // default 60. Set 0 to disable preemptive cooldown.
  onSendCode: (phone: string) => Promise<{
    phoneChangeSessionId: string;
    expiresAt: string; // ISO
  }>;
  onVerifyCode: (
    sessionId: string,
    code: string,
  ) => Promise<{
    phoneChangeToken: string;
    expiresAt: string; // ISO
  }>;
  // Fires on successful verify (token), and on phone-change-after-verify (null).
  onVerified: (token: string | null) => void;
  // Forwarded for parent to map to phone-field error / toast.
  onSendError?: (err: unknown) => void;
  onSendSuccess?: () => void;
  onVerifyError?: (err: unknown) => void;
  labels: PhoneVerificationLabels;
}

export interface PhoneVerificationController {
  sessionActive: boolean;
  token: string | null;
  isVerified: boolean;
  isSending: boolean;
  isVerifying: boolean;
  cooldownRemaining: number;
  codeRemaining: number;
  codeInput: string;
  codeExpired: boolean;
  verifyMessage: { kind: 'success' | 'error'; text: string } | null;
  sendButtonLabel: string;
  canSend: boolean;
  canVerify: boolean;
  send: () => Promise<void>;
  verify: () => Promise<void>;
  setCodeInput: (raw: string) => void;
  reset: () => void;
  labels: PhoneVerificationLabels;
}

const formatTimer = (sec: number): string => {
  const m = String(Math.floor(sec / 60)).padStart(2, '0');
  const s = String(sec % 60).padStart(2, '0');
  return `${m}:${s}`;
};

export function usePhoneVerification(deps: PhoneVerificationDeps): PhoneVerificationController {
  const {
    phone,
    cooldownSec = 60,
    onSendCode,
    onVerifyCode,
    onVerified,
    onSendError,
    onSendSuccess,
    onVerifyError,
    labels,
  } = deps;

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [codeInput, setCodeInputRaw] = useState('');
  const [codeExpiresAt, setCodeExpiresAt] = useState<number | null>(null);
  const [cooldownExpiresAt, setCooldownExpiresAt] = useState<number | null>(null);
  const [now, setNow] = useState<number>(() => Date.now());
  const [verifyMessage, setVerifyMessage] = useState<{
    kind: 'success' | 'error';
    text: string;
  } | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  // Phone we last issued a code for. When the parent changes `phone` away
  // from this, we drop the session/token so the user re-verifies.
  const lastSentPhoneRef = useRef<string | null>(null);

  const reset = useCallback(() => {
    setSessionId(null);
    setToken(null);
    setCodeInputRaw('');
    setCodeExpiresAt(null);
    setCooldownExpiresAt(null);
    setVerifyMessage(null);
    setIsSending(false);
    setIsVerifying(false);
    lastSentPhoneRef.current = null;
  }, []);

  useEffect(() => {
    if (lastSentPhoneRef.current === null) return;
    if (phone === lastSentPhoneRef.current) return;
    const hadToken = token !== null;
    setSessionId(null);
    setToken(null);
    setCodeInputRaw('');
    setCodeExpiresAt(null);
    setVerifyMessage(null);
    lastSentPhoneRef.current = null;
    if (hadToken) onVerified(null);
  }, [phone, token, onVerified]);

  // Tick once a second whenever a timer is active. We use absolute epoch ms
  // for expiry and derive remaining seconds — tab focus changes don't drift.
  const hasActiveTimer = codeExpiresAt !== null || cooldownExpiresAt !== null;
  useEffect(() => {
    if (!hasActiveTimer) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [hasActiveTimer]);

  const codeRemaining = codeExpiresAt
    ? Math.max(0, Math.ceil((codeExpiresAt - now) / 1000))
    : 0;
  const cooldownRemaining = cooldownExpiresAt
    ? Math.max(0, Math.ceil((cooldownExpiresAt - now) / 1000))
    : 0;
  const codeExpired =
    sessionId !== null && codeExpiresAt !== null && codeRemaining === 0 && token === null;

  const canSend = !isSending && token === null && cooldownRemaining === 0;
  const canVerify =
    !isVerifying &&
    token === null &&
    sessionId !== null &&
    !codeExpired &&
    codeInput.length === 6;

  const send = useCallback(async () => {
    if (!canSend) return;
    setIsSending(true);
    try {
      const { phoneChangeSessionId, expiresAt } = await onSendCode(phone);
      lastSentPhoneRef.current = phone;
      setSessionId(phoneChangeSessionId);
      setCodeExpiresAt(Date.parse(expiresAt));
      if (cooldownSec > 0) setCooldownExpiresAt(Date.now() + cooldownSec * 1000);
      setCodeInputRaw('');
      setToken(null);
      setVerifyMessage(null);
      onSendSuccess?.();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'SIGNUP_SMS_RATE_LIMIT') {
        const retry = Number(err.meta?.retryAfterSeconds ?? cooldownSec ?? 60);
        setCooldownExpiresAt(Date.now() + retry * 1000);
      }
      onSendError?.(err);
    } finally {
      setIsSending(false);
    }
  }, [canSend, phone, cooldownSec, onSendCode, onSendError, onSendSuccess]);

  const verify = useCallback(async () => {
    if (!canVerify || sessionId === null) return;
    setIsVerifying(true);
    try {
      const { phoneChangeToken } = await onVerifyCode(sessionId, codeInput);
      setToken(phoneChangeToken);
      setVerifyMessage({ kind: 'success', text: labels.verified });
      onVerified(phoneChangeToken);
    } catch (err) {
      if (onVerifyError) {
        onVerifyError(err);
        setCodeInputRaw('');
        return;
      }
      if (err instanceof ApiError) {
        switch (err.code) {
          case 'OTP_INVALID':
            setVerifyMessage({ kind: 'error', text: labels.codeInvalid });
            break;
          case 'OTP_EXPIRED':
            setVerifyMessage({ kind: 'error', text: labels.codeExpired });
            break;
          case 'SIGNUP_PHONE_UNVERIFIED':
            // Phone-keyed model: the verified flag was missing/expired — treat as session-expired UX.
            setVerifyMessage({ kind: 'error', text: labels.sessionExpired });
            setSessionId(null);
            setCodeExpiresAt(null);
            break;
          case 'UNAUTHENTICATED':
            // httpClient interceptor handles the logout/redirect; stay quiet.
            break;
          default:
            setVerifyMessage({ kind: 'error', text: labels.serverError });
        }
      } else {
        setVerifyMessage({ kind: 'error', text: labels.serverError });
      }
    } finally {
      setIsVerifying(false);
    }
  }, [canVerify, sessionId, codeInput, onVerifyCode, onVerified, labels]);

  // BE will also reject the next verify with CODE_EXPIRED, but surface the
  // inline expiry message the moment the local timer hits zero.
  useEffect(() => {
    if (codeExpired) setVerifyMessage({ kind: 'error', text: labels.codeExpired });
  }, [codeExpired, labels.codeExpired]);

  const setCodeInput = useCallback((raw: string) => {
    setCodeInputRaw(raw.replace(/\D/g, '').slice(0, 6));
    setVerifyMessage((prev) => (prev?.kind === 'error' ? null : prev));
  }, []);

  return {
    sessionActive: sessionId !== null,
    token,
    isVerified: token !== null,
    isSending,
    isVerifying,
    cooldownRemaining,
    codeRemaining,
    codeInput,
    codeExpired,
    verifyMessage,
    sendButtonLabel: sessionId ? labels.resend : labels.sendCode,
    canSend,
    canVerify,
    send,
    verify,
    setCodeInput,
    reset,
    labels,
  };
}

interface PhoneVerificationCodeSectionProps {
  controller: PhoneVerificationController;
  inputId?: string;
  className?: string;
}

export function PhoneVerificationCodeSection({
  controller,
  inputId = 'phone-verify-code',
  className = 'form-field',
}: PhoneVerificationCodeSectionProps) {
  const {
    sessionActive,
    isVerified,
    isVerifying,
    codeInput,
    codeRemaining,
    codeExpired,
    verifyMessage,
    canVerify,
    verify,
    setCodeInput,
    labels,
  } = controller;

  const codeRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (sessionActive && !isVerified) codeRef.current?.focus();
  }, [sessionActive, isVerified]);

  if (!sessionActive) return null;

  const messageId = `${inputId}-message`;

  return (
    <div className={className}>
      <label htmlFor={inputId} className="form-field__label">
        {labels.codeLabel}
      </label>
      <div className="inline-field">
        <input
          ref={codeRef}
          id={inputId}
          type="text"
          inputMode="numeric"
          maxLength={6}
          placeholder={labels.codePlaceholder}
          value={codeInput}
          aria-invalid={verifyMessage?.kind === 'error'}
          aria-describedby={verifyMessage ? messageId : undefined}
          disabled={isVerified || codeExpired}
          onChange={(e) => setCodeInput(e.target.value)}
          className="form-field__input"
        />
        <span className="timer" aria-live="polite">
          {isVerified ? (labels.verifiedTimer ?? '') : formatTimer(codeRemaining)}
        </span>
        <button
          type="button"
          className="btn btn--primary btn--sm"
          disabled={!canVerify}
          aria-busy={isVerifying}
          onClick={verify}
        >
          {labels.verify}
        </button>
      </div>
      {verifyMessage ? (
        verifyMessage.kind === 'success' ? (
          <p
            id={messageId}
            className="form-field__hint form-field__hint--success"
            role="status"
          >
            {verifyMessage.text}
          </p>
        ) : (
          <p id={messageId} className="form-field__error" role="alert">
            {verifyMessage.text}
          </p>
        )
      ) : null}
    </div>
  );
}
